import axios from 'axios';

// Local variables to hold auth functions, preventing circular dependencies
let _getCurrentUser = () => null;
let _logout = () => {};

export const configureApiService = (getUserFn, logoutFn) => {
  _getCurrentUser = getUserFn;
  _logout = logoutFn;
};

const BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const user = _getCurrentUser();
    if (user?.accessToken) {
      config.headers.Authorization = `Bearer ${user.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err.config;
    if (err.response) {
      // Handle both 401 and 403 as potential token expiration errors
      if ((err.response.status === 401 || err.response.status === 403) && !originalConfig._retry) {
        originalConfig._retry = true;
        
        const user = _getCurrentUser();
        
        if (user?.expiration) {
          const tokenExpirationTime = new Date(user.expiration).getTime();
          const currentTime = new Date().getTime();
          const gracePeriod = 30 * 60 * 1000; // 30 minutes

          if (currentTime > tokenExpirationTime + gracePeriod) {
            console.log("Session expired due to inactivity. Logging out.");
            _logout();
            return Promise.reject(new Error("Session expired due to inactivity."));
          }
        }

        try {
          const rs = await axios.post(`${BASE_URL}/auth/refresh-token`, {
            refreshToken: user?.refreshToken,
          });
          const { accessToken, refreshToken, expiration } = rs.data;
          const updatedUser = { ...user, accessToken, refreshToken, expiration };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          originalConfig.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalConfig);
        } catch (_error) {
          console.log("Refresh token failed. Logging out.");
          _logout();
          return Promise.reject(_error);
        }
      }
    }
    return Promise.reject(err);
  }
);

export const apiService = {
    customers: {
        getMinDetails: () => api.get('/customer/customerMinDetails'),
        getBalance: (customerId) => api.get(`/billing/balance/${customerId}`),
        list: (params) => api.get('/customer', { params }),
        create: (data) => api.post('/customer', data),
        update: (_id, data) => api.post('/customer', data),
        checkMobile: (mobileNo) => api.get(`/customer/check-mobile?mobileNo=${mobileNo}`),
    },
    collections: {
        saveCollection: (data) => api.post('/billing/billSummaryCollection', data),
        getTodayCollections: ({ date, billTypeFlag, customerId } = {}) => {
            const qp = new URLSearchParams();
            if (date) qp.set('date', date);
            if (billTypeFlag) qp.set('billTypeFlag', billTypeFlag);
            if (customerId) qp.set('customerId', customerId);
            const qs = qp.toString();
            return api.get(`/billing/todayCollection${qs ? `?${qs}` : ''}`);
        },
        deleteBillSummary: (id) => api.delete(`/billing/billSummary/${id}`),
        getActiveBillsToday: ({ date, billTypeFlag, isPrinted, isPaid, page, size } = {}) => {
            const qp = new URLSearchParams();
            if (date) qp.set('date', date);
            if (billTypeFlag) qp.set('billTypeFlag', billTypeFlag);
            if (isPrinted) qp.set('isPrinted', isPrinted);
            if (isPaid) qp.set('isPaid', isPaid);
            if (typeof page === 'number') qp.set('page', String(page));
            if (typeof size === 'number') qp.set('size', String(size));
            const qs = qp.toString();
            return api.get(`/billing/active-bills-today${qs ? `?${qs}` : ''}`);
        },
        getActiveBillsAggregate: ({ date, isPrinted, isPaid } = {}) => {
            const qp = new URLSearchParams();
            if (date) qp.set('date', date);
            if (isPrinted) qp.set('isPrinted', isPrinted);
            if (isPaid) qp.set('isPaid', isPaid);
            const qs = qp.toString();
            return api.get(`/billing/active-bills-aggregate${qs ? `?${qs}` : ''}`);
        },
        getBillSummariesByProduct: (productId, { page, size } = {}) => {
            const queryParams = new URLSearchParams();
            
            if (page !== undefined) {
                queryParams.append('page', page);
            }
            if (size !== undefined) {
                queryParams.append('size', size);
            }
            
            return api.get(`/billing/by-product/${productId}?${queryParams.toString()}`);
        },
        getCustomerLedger: (params) => api.get('/billing/customer-ledger', { params }),
        getCustomerRecentBills: (customerId, params) => api.get(`/billing/customer/${customerId}/recent-bills`, { params }),
        changeCustomer: (billSummaryId, newCustomerId) => api.put(`/billing/bill-summary/${billSummaryId}/change-customer`, { newCustomerId }),
    },
    products: {
        list: (params) => api.get('/product', { params }),
        create: (data) => api.post('/product', data),
        update: (data) => api.post('/product', data),
        delete: (id) => api.delete(`/product/${id}`),
        bulkUpdate: (data) => api.put('/products/bulk-update', data),
    },
    customerBill: {
        addItem: (data) => api.post('/customer-bill/add-item', data),
        receivePayment: (data) => api.post('/customer-bill/receive-payment', data),
    },
    quickBill: {
        addItem: (data) => api.post('/quick-bill/add-item', data),
    },
    productBill: {
        addItem: (data) => api.post('/product-bill/add-item', data),
    },
    salesDetails: {
        createOrUpdate: (data) => api.post('/sales-detail', data),
        getSalesByBillSummary: (billSummaryId, { billTypeFlag } = {}) => {
            const qp = new URLSearchParams();
            if (billTypeFlag) qp.set('billTypeFlag', billTypeFlag);
            const qs = qp.toString();
            return api.get(`/sales-detail/by-bill-summary/${billSummaryId}${qs ? `?${qs}` : ''}`);
        },
        listByCustomer: (params = {}) => {
            const { customerId, page = 0, size = 20, sortBy = 'salesDetailId', direction = 'DESC' } = params;
            const query = new URLSearchParams({
                customerId: String(customerId ?? ''),
                page: String(page),
                size: String(size),
                sortBy,
                direction
            }).toString();
            return api.get(`/sales-detail/by-customer?${query}`);
        },
        listByCustomerAndDate: (params = {}) => {
            const { customerId, billSummaryId, date, isPrinted, isPaid, page = 0, size = 20, sortBy = 'updateTimestamp', direction = 'DESC' } = params;
            const qp = new URLSearchParams();

            // Always include date if available
            if (date) qp.set('date', String(date));

            if (billSummaryId) {
                qp.set('billSummaryId', String(billSummaryId));
            } else if (customerId) {
                qp.set('customerId', String(customerId));
            }

            qp.set('page', String(page));
            qp.set('size', String(size));
            qp.set('sortBy', sortBy);
            qp.set('direction', direction);

            if (isPrinted) qp.set('isPrinted', isPrinted);
            if (isPaid) qp.set('isPaid', isPaid);

            return api.get(`/sales-detail/by-customer-and-date?${qp.toString()}`);
        },
        listByProductAndDate: (params = {}) => {
            const { productId, date, page = 0, size = 20, sortBy = 'updateTimestamp', direction = 'DESC' } = params;
            const qp = new URLSearchParams({
                productId: String(productId ?? ''),
                date: String(date ?? ''),
                page: String(page),
                size: String(size),
                sortBy,
                direction,
            });
            return api.get(`/sales-detail/by-product-and-date?${qp.toString()}`);
        },
        productsSoldToday: (date) => api.get(`/sales-detail/products-sold-today?date=${date}`),
        delete: (id) => api.delete(`/sales-detail/${id}`),
    },
};

export default apiService;
